/* eslint-disable react/sort-comp */
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';

import CalendarHeader from './CalendarHeader';
import Month from './Month';
import Time from './Time';

const START = 'start';
const END = 'end';
const HOURS = 12;
const DAYS = 7;

export const isWithinAllowedDateRange = (date, earliestDateTime, latestDateTime) => {
  if (earliestDateTime && (date.isBefore(earliestDateTime))) {
    return `Date-time must be after ${earliestDateTime.format('dddd, MMMM Do YYYY hh:mm a')}`;
  } else if (latestDateTime && (date.isAfter(latestDateTime))) {
    return `Date-time must be before ${latestDateTime.format('dddd, MMMM Do YYYY hh:mm a')}`;
  }

  return false;
};

class Calendar extends React.Component {
  constructor(props) {
    super(props);

    this.goToNextMonth = this.goToNextMonth.bind(this);
    this.goToPreviousMonth = this.goToPreviousMonth.bind(this);
    this.handleKeyDownOnDay = this.handleKeyDownOnDay.bind(this);
    this.handleUpdateHighlightedDay = this.handleUpdateHighlightedDay.bind(this);
    this.setDate = this.setDate.bind(this);
    this.setMinutes = this.setMinutes.bind(this);
    this.setHours = this.setHours.bind(this);
    this.toggleMeridiem = this.toggleMeridiem.bind(this);
    this.setEndMinutes = this.setEndMinutes.bind(this);
    this.setEndHours = this.setEndHours.bind(this);
    this.toggleMeridiemEnd = this.toggleMeridiemEnd.bind(this);

    this.state = {
      highlightedDate: props.displayedMonth || (props.selectedDates && props.selectedDates.start) || moment(),
    };
  }

  goToNextMonth() {
    this.setState((state) => ({
      highlightedDate: state.highlightedDate.clone().add(1, 'month'),
    }));
  }

  goToPreviousMonth() {
    this.setState((state) => ({
      highlightedDate: state.highlightedDate.clone().subtract(1, 'month'),
    }));
  }

  setDate(newDate) {
    const {
      calendarIdentifier, earliestDateTime, enableTime, handleUpdateDate, latestDateTime, selectedDates,
    } = this.props;
    const newDateToValidate = newDate.clone();
    if (enableTime && selectedDates && selectedDates[calendarIdentifier]) {
      newDateToValidate.hour(selectedDates[calendarIdentifier].hours()).minute(selectedDates[calendarIdentifier].minutes());
    }
    const errorMessage = isWithinAllowedDateRange(newDateToValidate, earliestDateTime, latestDateTime);
    handleUpdateDate(newDate, errorMessage);
  }

  setHours(hour) {
    const {
      calendarIdentifier, earliestDateTime, handleUpdateHours, latestDateTime, selectedDates,
    } = this.props;
    const newDate = selectedDates[calendarIdentifier].clone().set('hour', hour);
    const errorMessage = isWithinAllowedDateRange(newDate, earliestDateTime, latestDateTime);
    handleUpdateHours(hour, calendarIdentifier, errorMessage);
  }

  setMinutes(minute) {
    const {
      calendarIdentifier, earliestDateTime, handleUpdateMinutes, latestDateTime, selectedDates,
    } = this.props;
    const newDate = selectedDates[calendarIdentifier].clone().set('minute', minute);
    const errorMessage = isWithinAllowedDateRange(newDate, earliestDateTime, latestDateTime);
    handleUpdateMinutes(minute, calendarIdentifier, errorMessage);
  }

  toggleMeridiem() {
    const {
      calendarIdentifier, earliestDateTime, handleUpdateHours, latestDateTime, selectedDates,
    } = this.props;
    const date = selectedDates[calendarIdentifier];
    const newDate = date.format('a') === 'am' ? date.clone().add(HOURS, 'hours') : date.clone().subtract(HOURS, 'hours');

    const errorMessage = isWithinAllowedDateRange(newDate, earliestDateTime, latestDateTime);
    handleUpdateHours(newDate.hour(), calendarIdentifier, errorMessage);
  }

  setEndHours(hour) {
    const {
      calendarIdentifierEnd, earliestDateTime, handleUpdateHours, latestDateTime, selectedDates,
    } = this.props;
    const newDate = selectedDates[calendarIdentifierEnd].clone().set('hour', hour);
    const errorMessage = isWithinAllowedDateRange(newDate, earliestDateTime, latestDateTime);
    handleUpdateHours(hour, calendarIdentifierEnd, errorMessage);
  }

  setEndMinutes(minute) {
    const {
      calendarIdentifierEnd, earliestDateTime, handleUpdateMinutes, latestDateTime, selectedDates,
    } = this.props;
    const newDate = selectedDates[calendarIdentifierEnd].clone().set('minute', minute);
    const errorMessage = isWithinAllowedDateRange(newDate, earliestDateTime, latestDateTime);
    handleUpdateMinutes(minute, calendarIdentifierEnd, errorMessage);
  }

  toggleMeridiemEnd() {
    const {
      calendarIdentifierEnd, earliestDateTime, handleUpdateHours, latestDateTime, selectedDates,
    } = this.props;
    const date = selectedDates[calendarIdentifierEnd];
    const newDate = date.format('a') === 'am' ? date.clone().add(HOURS, 'hours') : date.clone().subtract(HOURS, 'hours');

    const errorMessage = isWithinAllowedDateRange(newDate, earliestDateTime, latestDateTime);
    handleUpdateHours(newDate.hour(), calendarIdentifierEnd, errorMessage);
  }

  handleUpdateHighlightedDay(newDate) {
    this.setState({ highlightedDate: newDate }, () => {
      document.getElementById(`day-${newDate.format('MM-DD-YYYY')}`).focus();
    });
  }

  handleKeyDownOnDay(event) {
    const { calendarIdentifier, enableTime } = this.props;
    const { highlightedDate } = this.state;
    const day = highlightedDate;

    const TAB = 9;
    const ENTER = 13;
    const LEFT_ARROW = 37;
    const UP_ARROW = 38;
    const RIGHT_ARROW = 39;
    const DOWN_ARROW = 40;
    switch (event.keyCode) {
      case ENTER: {
        this.setDate(day);
        if (enableTime) {
          document.getElementById(`input-hour-${calendarIdentifier}`).focus();
        }
        break;
      }
      case LEFT_ARROW: {
        const prevDay = day.clone().subtract(1, 'day');
        this.handleUpdateHighlightedDay(prevDay);
        break;
      }
      case RIGHT_ARROW: {
        const nextDay = day.clone().add(1, 'day');
        this.handleUpdateHighlightedDay(nextDay);
        break;
      }
      case UP_ARROW: {
        const lastWeek = day.clone().subtract(DAYS, 'day');
        this.handleUpdateHighlightedDay(lastWeek);
        break;
      }
      case DOWN_ARROW: {
        const nextWeek = day.clone().add(DAYS, 'day');
        this.handleUpdateHighlightedDay(nextWeek);
        break;
      }
      case TAB: {
        event.preventDefault();
        if (enableTime) {
          document.getElementById(`input-hour-${calendarIdentifier}`).focus();
        } else {
          document.activeElement.blur();
        }
        break;
      }
      default:
        break;
    }
  }

  render() {
    const {
      calendarIdentifierEnd, calendarIdentifier, earliestDateTime, enableOutsideDays, enableRange, enableTime, latestDateTime, selectedDates,
    } = this.props;
    const { highlightedDate } = this.state;

    const date = selectedDates ? selectedDates[calendarIdentifier] : highlightedDate;
    const endDate = selectedDates ? selectedDates[calendarIdentifierEnd] : highlightedDate;
    const isEndTimeInvalid = calendarIdentifier === END && selectedDates.start && selectedDates.start.isAfter(selectedDates.end);

    return (
      <div className="p2-sm">
        <CalendarHeader
          handleGoToNextMonth={this.goToNextMonth}
          handleGoToPreviousMonth={this.goToPreviousMonth}
          month={highlightedDate.format('MMMM YYYY')}
        />
        <Month
          earliestDateTime={earliestDateTime}
          enableOutsideDays={enableOutsideDays}
          handleDayClick={this.setDate}
          handleKeyDownOnDay={this.handleKeyDownOnDay}
          latestDateTime={latestDateTime}
          month={highlightedDate}
          selectedDates={selectedDates}
        />
        {enableTime && (
          <Time
            calendarIdentifier={calendarIdentifier}
            date={date}
            handleSetHours={this.setHours}
            handleSetMinutes={this.setMinutes}
            handleToggleMeridiem={this.toggleMeridiem}
            isTimeInvalid={isEndTimeInvalid}
            label={enableRange ? 'From' : ''}
          />
        )}
        {enableTime && enableRange && (
          <Time
            calendarIdentifier={calendarIdentifierEnd}
            date={endDate}
            handleSetHours={this.setEndHours}
            handleSetMinutes={this.setEndMinutes}
            handleToggleMeridiem={this.toggleMeridiemEnd}
            isTimeInvalid={isEndTimeInvalid}
            label="To"
          />
        )}
      </div>
    );
  }
}

Calendar.propTypes = {
  calendarIdentifier: PropTypes.string,
  calendarIdentifierEnd: PropTypes.string,
  displayedMonth: PropTypes.shape().isRequired,
  earliestDateTime: PropTypes.shape(),
  enableOutsideDays: PropTypes.bool.isRequired,
  enableRange: PropTypes.bool.isRequired,
  enableTime: PropTypes.bool.isRequired,
  handleUpdateDate: PropTypes.func.isRequired,
  handleUpdateHours: PropTypes.func.isRequired,
  handleUpdateMinutes: PropTypes.func.isRequired,
  latestDateTime: PropTypes.shape(),
  selectedDates: PropTypes.shape().isRequired,
};

Calendar.defaultProps = {
  calendarIdentifier: START,
  calendarIdentifierEnd: END,
  earliestDateTime: null,
  latestDateTime: null,
};

export default Calendar;
